import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Empty, Typography, message } from 'antd';
import PujaSpinner from './PujaSpinner';
import PlanillaCarga from './PlanillaCarga';
import { criaService } from '../services/api';

const { Paragraph, Text } = Typography;

/**
 * Solapa «Crías» de la ficha del animal (2026-10-21): las crías al pie de una
 * madre en el remate donde está HOY, cargadas en planilla.
 *
 * Existe por las ELECCIONES: la madre sale con varios terneros y el que gana la
 * puja elige uno. Hasta ese día el formulario del animal editaba UNA cría.
 *
 * Guardar REEMPLAZA el set completo (`criaService.reemplazar`), todo o nada: el
 * orden de las filas es el orden en que se muestran, y vaciar la planilla las
 * borra a todas. Todos los campos son opcionales ("dato que está se muestra"):
 * en una elección a veces sólo se sabe el RP y el sexo. Las reglas de cada
 * celda (incluido el RP repetido) son espejo de `normalizar_cria` y
 * `rps_repetidos` (cria_routes.py), que son las que mandan.
 */

const SEXOS = ['m', 'macho', 'h', 'hembra'];
const PELOS = ['1', '2', 'c', 'n', 'colorado', 'negro'];
const NUMERO = /^\d{1,3}([.,]\d{1,2})?$/;

const tope = (n, label) => (v) => (v.length > n ? `${label}: máximo ${n} caracteres` : null);

const COLUMNAS = [
  {
    key: 'rp_cria', label: 'RP', width: 130, placeholder: '761',
    validate: (v, filas, id) => tope(50, 'RP')(v)
      || (filas.some(f => f.id !== id && (f.cells.rp_cria || '').trim() === v) ? 'RP repetido en la planilla' : null),
  },
  {
    key: 'sexo_cria', label: 'Sexo', width: 90, placeholder: 'M / H',
    validate: (v) => (SEXOS.includes(v.toLowerCase()) ? null : 'M (macho) o H (hembra)'),
  },
  { key: 'nacimiento_cria', label: 'Nacimiento', width: 150, placeholder: '12/08/2026 o 2026', validate: tope(100, 'Nacimiento') },
  { key: 'padre_cria', label: 'Padre', width: 220, placeholder: 'Nombre del padre', validate: tope(255, 'Padre') },
  {
    key: 'peso_nacer_cria', label: 'Peso al nacer', width: 110, placeholder: 'kg',
    validate: (v) => (NUMERO.test(v) ? null : 'Número de 0 a 999'),
  },
  {
    key: 'pelo_cria', label: 'Pelo', width: 110, placeholder: 'Colorado / Negro',
    validate: (v) => (PELOS.includes(v.toLowerCase()) ? null : 'Colorado o Negro'),
  },
  { key: 'reg_cria', label: 'Registro', width: 90, placeholder: 'PP', validate: tope(2, 'Registro') },
];

// Lo que vuelve del backend → texto de celda. El sexo va en letra y el pelo
// con su nombre: es lo que el productor tipearía.
const aFila = (c) => ({
  rp_cria: c.rp_cria,
  sexo_cria: c.sexo_cria,
  nacimiento_cria: c.nacimiento_cria,
  padre_cria: c.padre_cria,
  peso_nacer_cria: c.peso_nacer_cria,
  pelo_cria: c.pelo_cria_label,
  reg_cria: c.reg_cria,
});

const CriasDelAnimal = ({ animal }) => {
  const idAnimal = animal.id_animal;
  const idEvento = animal.id_evento;
  const [crias, setCrias] = useState(null);
  const [serverErrors, setServerErrors] = useState([]);
  // Cambia después de cada guardado: la planilla lee sus filas al montar, así
  // que una `key` nueva la vuelve a armar con lo que quedó en la base.
  const [version, setVersion] = useState(0);

  const cargar = useCallback(async () => {
    if (!idEvento) return;
    try {
      const res = await criaService.getByAnimalEvento(idAnimal, idEvento);
      setCrias(res.data?.crias || []);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al cargar las crías');
      setCrias([]);
    }
  }, [idAnimal, idEvento]);

  useEffect(() => { cargar(); }, [cargar]);

  const filasIniciales = useMemo(() => (crias || []).map(aFila), [crias]);

  const guardar = async (filas) => {
    setServerErrors([]);
    try {
      const res = await criaService.reemplazar(idAnimal, idEvento, filas);
      const n = res.data?.crias?.length ?? filas.length;
      message.success(n ? `${n} ${n === 1 ? 'cría guardada' : 'crías guardadas'}` : 'Crías borradas');
      setCrias(res.data?.crias || []);
      setVersion(v => v + 1);
    } catch (error) {
      const data = error.response?.data;
      if (data?.errores?.length) {
        setServerErrors(data.errores);
        message.error(`${data.filas_con_error} filas con errores. No se guardó nada.`);
      } else {
        message.error(data?.error || 'Error al guardar las crías');
      }
    }
  };

  if (!idEvento) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Las crías al pie se venden con la madre en un remate: asigná el animal a un remate para cargarlas."
      />
    );
  }
  if (crias === null) return <PujaSpinner />;

  return (
    <Card>
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        Las crías al pie de esta madre en el remate donde está hoy. Una fila por ternero, en el orden en que querés mostrarlos. Ningún dato es obligatorio:
        en una <Text strong>elección</Text> alcanza con el RP y el sexo. Podés copiar desde
        Excel y pegar en cualquier celda. Al guardar se reemplazan todas las crías cargadas.
      </Paragraph>
      <PlanillaCarga
        key={version}
        columns={COLUMNAS}
        initialRows={filasIniciales}
        initialRowCount={3}
        maxRows={100}
        permitirVacio={crias.length > 0}
        onSubmit={guardar}
        submitLabel="Guardar crías"
        serverErrors={serverErrors}
      />
    </Card>
  );
};

export default CriasDelAnimal;
